/**
 * usePointerSort.js - drag to reorder that feels like moving a physical block
 * (round 4, 10/9/26: "a lifted card follows the pointer, the others slide
 * apart, it settles into place"). Used by the Customize column list (axis
 * 'y') and the table headers (axis 'x'). Pointer Events, so mouse, pen and
 * touch; no library.
 *
 *   const sort = usePointerSort({ axis, onDrop(key, toIndex) });
 *   <ul ref={sort.ref} onPointerDown={sort.onPointerDown}>
 *     <li data-sort-key="quotedPrice">...</li>
 *
 * How it moves:
 *   - nothing happens until the pointer has travelled 5px, so a click (a
 *     header's sort, a checkbox) is still a click; a drag swallows the click
 *     that follows it
 *   - the item is copied into a lifted "card" (shadow, a touch larger, a
 *     slight tilt - index.css .tv-drag-ghost) that follows the pointer once per
 *     animation frame; the item itself stays as a faded placeholder
 *   - the items between where it was and where it would land slide over by
 *     its size (a CSS transform with a short ease-out transition), opening
 *     the gap; for headers a line also marks the landing spot down the table
 *   - on release the card glides into the gap, the transforms are cleared in
 *     the same frame the new order renders (so nothing jumps), and the card
 *     fades out over the item now in its place
 * Buttons, fields, links and the resize grip never start a drag. With
 * prefers-reduced-motion the transitions are instant (index.css).
 */
import { useCallback, useRef } from 'react';

const NO_DRAG = 'button, input, select, textarea, a, label, [role="switch"], .col-resize-grip, [data-no-drag]';
const THRESHOLD = 5;
const SLIDE = 'transform 170ms cubic-bezier(0.2, 0.8, 0.2, 1)';

const raf = (fn) => (typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(fn) : (fn(), 0));

export function usePointerSort({ axis = 'y', onDrop, indicator = false }) {
  const ref = useRef(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const onPointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const box = ref.current;
      const item = e.target && e.target.closest ? e.target.closest('[data-sort-key]') : null;
      if (!box || !item || !box.contains(item) || e.target.closest(NO_DRAG)) return;
      const startX = e.clientX;
      const startY = e.clientY;
      let drag = null; // set once the pointer has moved far enough
      let frame = 0;
      let last = { x: startX, y: startY };

      const begin = () => {
        const items = [...box.querySelectorAll('[data-sort-key]')];
        const rects = items.map((el) => el.getBoundingClientRect());
        const from = items.indexOf(item);
        const r = rects[from];
        const size = axis === 'x' ? r.width : r.height;
        // the lifted card
        const ghost = document.createElement('div');
        ghost.className = 'tv-drag-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.textContent = (item.getAttribute('data-sort-label') || item.textContent || '').trim();
        Object.assign(ghost.style, {
          position: 'fixed',
          left: `${r.left}px`,
          top: `${r.top}px`,
          width: `${Math.max(r.width, 60)}px`,
          height: `${Math.max(r.height, 24)}px`,
          zIndex: 3000,
          pointerEvents: 'none',
        });
        document.body.appendChild(ghost);
        // the landing line, down the visible table (headers)
        let line = null;
        if (indicator) {
          const pane = box.closest('.overflow-auto') || box.closest('table') || box;
          const pr = pane.getBoundingClientRect();
          line = document.createElement('div');
          line.className = 'tv-drop-line';
          Object.assign(line.style, {
            position: 'fixed',
            top: `${pr.top}px`,
            height: `${Math.max(pr.height, r.height)}px`,
            left: `${r.left}px`,
            zIndex: 2999,
            pointerEvents: 'none',
          });
          document.body.appendChild(line);
        }
        items.forEach((el) => {
          if (el !== item) el.style.transition = SLIDE;
        });
        item.setAttribute('data-sort-dragging', '');
        document.body.style.userSelect = 'none';
        document.body.classList.add('tv-sorting');
        drag = { items, rects, from, to: from, size, ghost, line, r };
      };

      const paint = () => {
        frame = 0;
        if (!drag) return;
        const { items, rects, from, size, ghost, line, r } = drag;
        const dx = last.x - startX;
        const dy = last.y - startY;
        ghost.style.transform = `translate(${axis === 'x' ? dx : 0}px, ${axis === 'y' ? dy : 0}px)`;
        // where it would land: the number of other items whose middle it has passed
        const pos = axis === 'x' ? r.left + r.width / 2 + dx : r.top + r.height / 2 + dy;
        let to = 0;
        rects.forEach((rc, i) => {
          if (i === from) return;
          const mid = axis === 'x' ? rc.left + rc.width / 2 : rc.top + rc.height / 2;
          if (pos > mid) to += 1;
        });
        drag.to = to;
        items.forEach((el, i) => {
          if (i === from) return;
          let shift = 0;
          if (from < to && i > from && i <= to) shift = -size;
          if (from > to && i >= to && i < from) shift = size;
          el.style.transform = shift ? (axis === 'x' ? `translateX(${shift}px)` : `translateY(${shift}px)`) : '';
        });
        if (line) {
          const others = rects.filter((_, i) => i !== from);
          const edge = to >= others.length ? (others.length ? others[others.length - 1].right : r.right) : others[to].left;
          line.style.left = `${edge - 1}px`;
        }
      };

      const move = (ev) => {
        last = { x: ev.clientX, y: ev.clientY };
        if (!drag) {
          if (Math.abs(last.x - startX) < THRESHOLD && Math.abs(last.y - startY) < THRESHOLD) return;
          begin();
        }
        if (ev.cancelable) ev.preventDefault();
        if (!frame) frame = raf(paint);
      };

      const up = () => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        document.removeEventListener('pointercancel', up);
        if (!drag) return;
        if (frame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
        frame = 0;
        paint();
        const { items, rects, from, to, ghost, line, r } = drag;
        // a drag is not a click: swallow the click that follows it
        const swallow = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
        // the card glides into the gap, then fades over the item there
        const others = rects.filter((_, i) => i !== from);
        let target = r;
        if (to !== from) {
          const at = others[Math.min(to, others.length - 1)];
          if (at)
            target =
              axis === 'x'
                ? { left: to > from ? at.right - r.width : at.left, top: r.top }
                : { left: r.left, top: to > from ? at.bottom - r.height : at.top };
        }
        ghost.classList.add('tv-drag-ghost-settle');
        ghost.style.transform = `translate(${target.left - r.left}px, ${target.top - r.top}px)`;
        setTimeout(() => ghost.remove(), 190);
        if (line) line.remove();
        // clear the slides in the same frame the new order is drawn: no jump
        items.forEach((el) => {
          el.style.transition = 'none';
          el.style.transform = '';
        });
        raf(() =>
          items.forEach((el) => {
            el.style.transition = '';
          })
        );
        item.removeAttribute('data-sort-dragging');
        document.body.style.userSelect = '';
        document.body.classList.remove('tv-sorting');
        const key = item.getAttribute('data-sort-key');
        drag = null;
        if (to !== from && onDropRef.current) onDropRef.current(key, to);
      };

      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      document.addEventListener('pointercancel', up);
    },
    [axis, indicator]
  );

  return { ref, onPointerDown };
}

/** The new order after moving `key` to position `to` (in the list without it). PURE. */
export function moveTo(list, key, to) {
  const rest = list.filter((k) => k !== key);
  if (!list.includes(key)) return list;
  rest.splice(Math.max(0, Math.min(to, rest.length)), 0, key);
  return rest;
}
